import * as React from 'react'
import { ReactElement, useState } from 'react'
import {
    Button,
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    DialogTrigger,
    Field,
    Link,
} from '@fluentui/react-components'
import { bundleIcon, CheckmarkFilled, CheckmarkRegular, DismissFilled, DismissRegular } from '@fluentui/react-icons'
import { useBoolean } from '@fluentui/react-hooks'
import { InvoiceOverviewDto } from '../../../config/finance/invoiceConfig'
import { Icon } from '@fluentui/react'
import Tooltip from '../../Tooltip'

import { sanitize as DOMSanitize } from 'dompurify'
import Textarea from '../../Text/TextArea'
import useAxios from '../../../hooks/useAxios'
import { useTranslation } from 'react-i18next'

type Props = {
    invoice: InvoiceOverviewDto
    onSubmit: (value: string) => void
}

const CheckmarkIcon = bundleIcon(CheckmarkFilled, CheckmarkRegular)
const DismissIcon = bundleIcon(DismissFilled, DismissRegular)

export default function InvoiceNoteDialog(props: Props): ReactElement {
    const axios = useAxios()
    const { t: translate } = useTranslation()

    const [internalOpen, { toggle: toggleOpen }] = useBoolean(false)
    const [submitting, { setTrue: setIsSubmitting, setFalse: setNotSubmitting }] = useBoolean(false)
    const [value, setValue] = useState<string>(props.invoice.note ?? '')

    return (
        <Dialog {...props} open={internalOpen} onOpenChange={() => toggleOpen()}>
            <DialogTrigger disableButtonEnhancement>
                <Link>
                    <Tooltip
                        hostProps={{
                            content: props.invoice.note ? (
                                <div
                                    dangerouslySetInnerHTML={{
                                        __html: DOMSanitize(props.invoice.note)?.replaceAll(/[\n\\n]/g, '<br/>'),
                                    }}
                                />
                            ) : (
                                translate('invoice.table.note.add')
                            ),
                        }}
                    >
                        {!!props.invoice.note ? <Icon iconName={'EditNote'} /> : <Icon iconName={'DietPlanNotebook'} />}
                    </Tooltip>
                </Link>
            </DialogTrigger>

            <DialogSurface>
                <DialogBody>
                    <DialogTitle>
                        {props.invoice.note
                            ? translate('invoice.table.note.edit')
                            : translate('invoice.table.note.add')}
                    </DialogTitle>
                    <DialogContent>
                        <Field label={translate('invoice.table.header.note')}>
                            <Textarea value={value} autoAdjustHeight onChange={(_, data) => setValue(data.value)} />
                        </Field>
                    </DialogContent>
                    <DialogActions>
                        <DialogTrigger disableButtonEnhancement>
                            <Button appearance="transparent" icon={<DismissIcon />}>
                                {translate('action.cancel')}
                            </Button>
                        </DialogTrigger>
                        <DialogTrigger disableButtonEnhancement>
                            <Button
                                appearance="primary"
                                disabled={submitting}
                                icon={<CheckmarkIcon />}
                                onClick={() => {
                                    setIsSubmitting()
                                    axios
                                        .put(`/finance/invoice/${props.invoice.id}/note`, { value: value })
                                        .then(() => props.onSubmit(value))
                                        .catch(e => console.log(e))
                                        .finally(() => setNotSubmitting())
                                }}
                            >
                                {translate('action.save')}
                            </Button>
                        </DialogTrigger>
                    </DialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    )
}
